/**
 * Reusable error state component.
 *
 * Props:
 *  - title: string (default: 'Something went wrong')
 *  - description: string (optional)
 *  - onRetry: function (optional) -- retry button click handler
 *  - showIcon: boolean (default: false) -- show alert icon box
 *  - className: string (optional) -- additional wrapper classes
 */
import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";

function ErrorState({
  title = "Something went wrong",
  description = "We couldn't load this data. Please try again.",
  onRetry,
  retryLabel = "Try again",
  showIcon = false,
  className = "",
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`flex flex-col items-center justify-center text-center py-12 px-6 ${className}`}
    >
      {showIcon ? (
        <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <AlertCircle className="w-7 h-7 text-red-500" />
        </div>
      ) : null}
      <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
        {title}
      </h3>
      {description && (
        <p className="text-sm text-[var(--text-secondary)] max-w-sm leading-relaxed">
          {description}
        </p>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 btn-secondary btn-compact"
        >
          {retryLabel}
        </button>
      )}
    </motion.div>
  );
}

export default ErrorState;
